import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface AdminImage {
  id: number;
  image_name: string;
  item: string | null;
  material: string | null;
  brand: string | null;
  color: string | null;
  product_age: string | null;
  product_condition: string | null;
  price: number | null;
  created_at: string;
}

export interface AdminImagesQueryParams {
  limit?: number;
  offset?: number;
  brand?: string;
  item?: string;
}

export interface AdminImagesResponse {
  images: AdminImage[];
  total: number;
  limit: number;
  offset: number;
}

export interface DeletePreviewTarget {
  id: number;
  image_name: string;
  photo_type: string;
  brand: string;
  item: string;
  material: string;
  color: string;
  price: number;
  product_age: string;
  product_condition: string;
  image_vector_id: number;
}

export interface DeletePreviewSiblingPhoto {
  id: number;
  image_name: string;
  photo_type: string;
}

export interface DeletePreviewSessionEntry {
  id: number;
  image_name: string;
  brand: string;
  item: string;
}

export interface DeletePreviewResponse {
  target: DeletePreviewTarget;
  sibling_photos: DeletePreviewSiblingPhoto[];
  session_entries: DeletePreviewSessionEntry[];
  image_vector_id: number;
  total_photos_to_delete: number;
  total_session_entries_to_delete: number;
}

@Injectable({
  providedIn: 'root'
})
export class AdminImagesService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getImages(query: AdminImagesQueryParams): Observable<AdminImagesResponse> {
    let params = new HttpParams();

    if (query.limit !== undefined) {
      params = params.set('limit', query.limit.toString());
    }

    if (query.offset !== undefined) {
      params = params.set('offset', query.offset.toString());
    }

    if (query.brand) {
      params = params.set('brand', query.brand);
    }

    if (query.item) {
      params = params.set('item', query.item);
    }

    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      params,
      withCredentials: true
    };

    return this.http.get<AdminImagesResponse>(`${this.apiUrl}/admin/images`, options);
  }

  getImageDownloadUrl(imageId: number): Observable<{ download_url: string; expires_in: number; image_name: string }> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      withCredentials: true
    };

    return this.http.get<{ download_url: string; expires_in: number; image_name: string }>(
      `${this.apiUrl}/admin/images/${imageId}/download-url`,
      options
    );
  }

  deleteImage(imageId: number): Observable<{ message: string }> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      withCredentials: true
    };

    return this.http.delete<{ message: string }>(`${this.apiUrl}/admin/images/${imageId}`, options);
  }

  editImage(imageId: number, details: {
    brand: string; item: string; material: string; color: string;
    price: number; age: string; condition: string;
  }): Observable<{ message: string; product: AdminImage }> {
    return this.http.patch<{ message: string; product: AdminImage }>(
      `${this.apiUrl}/admin/images/${imageId}`,
      details,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), withCredentials: true }
    );
  }

  /** GET /admin/images/{id}/delete-preview — returns what will be deleted before confirming */
  getDeletePreview(imageId: number): Observable<DeletePreviewResponse> {
    return this.http.get<DeletePreviewResponse>(
      `${this.apiUrl}/admin/images/${imageId}/delete-preview`,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), withCredentials: true }
    );
  }
}
